/**
 * Control labels — runtime cache backed by the YAML.
 *
 * Single source of truth for control titles and plain-language descriptions
 * is the YAML at `backend/src/data/unified-scripts/<controlKey>.yaml`. The
 * seeder loads them into `InterviewScriptEntity`, the
 * `GET /companies/:id/controls` endpoint exposes them, and
 * `useControlCatalog()` (`frontend/src/api/services/use-control-catalog-service.ts`)
 * populates the module-scoped cache below at app boot.
 *
 * Why a sync API + cache: many call sites (table renderers, AntD tag text,
 * i18n.t() interpolations, the SSR `ReportPreviewHtml` symbol-path file)
 * call `getControlLabel(key, lang)` synchronously and don't have an
 * easy place to await a hook. The cache lets every consumer keep the
 * sync signature while the data flows from one place: the YAML.
 *
 * Cold-start: until the API responds, the cache is empty and the helpers
 * fall back to a prettified key (e.g. `"Cc6 2 Periodic Access Review"`).
 * That is intentionally low-fidelity so the production code path is the
 * API path — no static list of names sneaks in as a "fallback that is
 * really a parallel source of truth".
 */

interface ControlLabelEntry {
  name: { en: string; es: string }
  plainDescription: { en: string | null; es: string | null }
  domain: 'operational_risk' | 'human_risk'
}

/**
 * Module-scoped cache populated by `setControlCatalog()` at app boot. Empty
 * until the first `useControlCatalog` fetch resolves; the helpers below
 * fall back to the prettified key in that window.
 */
const _cache = new Map<string, ControlLabelEntry>()

interface CatalogPayloadEntry {
  controlKey: string
  name: { en: string; es: string }
  plainDescription: { en: string | null; es: string | null }
  domain: 'operational_risk' | 'human_risk'
}

/**
 * Replace the cache with the latest catalog from the API. Idempotent —
 * called by `useControlCatalog` whenever the React Query hook resolves a
 * fresh payload.
 */
export const setControlCatalog = (entries: ReadonlyArray<CatalogPayloadEntry>): void => {
  _cache.clear()
  for (const e of entries) {
    _cache.set(e.controlKey, { name: e.name, plainDescription: e.plainDescription, domain: e.domain })
  }
}

/**
 * Convert a snake_case (or kebab-case) controlKey to a low-fidelity Title
 * Case label. Used as the cold-start fallback before the catalog cache
 * loads — never as a real source of truth.
 */
const prettify = (key: string): string =>
  key
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')

/** Get the label for the current language (defaults to 'es'). */
export const getControlLabel = (key: string, lang = 'es'): string => {
  const entry = _cache.get(key)
  if (!entry) return prettify(key)
  const isEs = lang.startsWith('es')
  return (isEs ? entry.name.es : entry.name.en) || entry.name.es || prettify(key)
}

/**
 * Get the non-technical plain-language description for a control. Returns
 * `''` when the catalog hasn't loaded yet OR when the YAML doesn't have a
 * `plain_description` block — call sites use `&&` to hide the subtitle.
 */
export const getControlPlainDescription = (key: string, lang = 'es'): string => {
  const entry = _cache.get(key)
  if (!entry) return ''
  const isEs = lang.startsWith('es')
  return (isEs ? entry.plainDescription.es : entry.plainDescription.en) ?? ''
}

/**
 * Returns control keys eligible for interview scheduling — only
 * `domain: operational_risk` entries. Human-risk controls are auto-evidenced
 * from precomputed signals and don't have interview questions, so they never
 * belong in the Control Assessment dropdown.
 */
export const getInterviewableControlKeys = (): string[] => {
  const out: string[] = []
  for (const [key, entry] of _cache) {
    if (entry.domain === 'operational_risk') out.push(key)
  }
  return out
}

/**
 * Phase 143 D-12 / D-13 :: Ciphia-data control codes.
 *
 * These three controls are NOT process-audit driven. Their evidence comes
 * from precomputed Ciphia data (Atlas graph, employee risk model,
 * phishing-simulation campaigns), so the report-builder routes them to
 * dedicated section components with bespoke visualizations rather than
 * the standard findings/no_audit/ai_narrative router.
 *
 * @invariant D-12 :: these three control codes drive Ciphia-data ordering
 * (front of report) AND Ciphia-data section component selection.
 *
 * Resolved against backend/src/data/unified-scripts/ controlKey values:
 * - org_structure        :: Organizational Risk Structure  (Atlas)
 * - per_employee_risk    :: Per-Employee Risk
 * - phishing_awareness   :: Phishing Awareness
 */
export const ATLAS_CONTROL_CODE = 'org_structure'
export const EMPLOYEE_RISK_CONTROL_CODE = 'per_employee_risk'
export const PHISHING_CONTROL_CODE = 'phishing_awareness'

/**
 * Ordered tuple :: front-of-report ordering MUST match this sequence
 * (D-12 :: Atlas → Employee Risk → Phishing).
 */
export const CIPHIA_DATA_CONTROL_ORDER: ReadonlyArray<string> = [
  ATLAS_CONTROL_CODE,
  EMPLOYEE_RISK_CONTROL_CODE,
  PHISHING_CONTROL_CODE,
] as const

/**
 * Set :: O(1) membership check for Ciphia-data control codes used by the
 * report builder page when ordering controls (Ciphia-data first) and by
 * future Wave 3 routing inside the Risk Analysis section.
 */
export const CIPHIA_DATA_CONTROLS = new Set<string>(CIPHIA_DATA_CONTROL_ORDER)
